<template>
  <div>
    <b-breadcrumb :items="breadcrumbItems"></b-breadcrumb>

    <div class="card">
      <div class="card-body">
        <div class="row">
          <div class="col-sm-6">
            <h4 qid="lb-view-page-title" class="mb-3">
              {{ legalBasis.name }}
            </h4>
            <div class="mb-2">
              <span v-if="getGuide('legal_basis')">
                <GuideButton :guide="getGuide('legal_basis')"></GuideButton>
              </span>
            </div>
          </div>

          <div class="col-md-6 text-right" qid="view-lb-modified">
            <div class="mb-4">
                <span class="h6 ml-3">
                <span v-if="isGranted([permissions.LEGAL_BASIS_EDIT, permissions.LEGAL_BASIS_EDIT_OWN], legalBasis.created_by) && (!getClient.parent || (getClient.parent && legalBasis.partition))"
                      class="btn btn-edit"
                      qid="edit-lb-button"
                      @click="showEditLBModal(legalBasis)"
                >
                  <font-awesome-icon icon="pen"/>
                </span>
                <span v-if="isGranted([permissions.LEGAL_BASIS_DELETE, permissions.LEGAL_BASIS_DELETE_OWN], legalBasis.created_by) && (!getClient.parent || (getClient.parent && legalBasis.partition))"
                      class="btn btn-delete"
                      qid="delete-lb-button"
                      @click="showConfirmDeleteModal(legalBasis)"
                >
                  <font-awesome-icon icon="trash-alt"/>
                </span>
              </span>
            </div>
            <div>
              <span>{{ $t('table.last_updated') }}</span>
              <span class="ml-2 text-muted small" qid="view-lb-modified-date">
                {{ legalBasis.updated_at | formatDate }}
              </span>
              <Avatar v-if="legalBasis.updated_by" :object="legalBasis.updated_by" size="xs" class="ml-3 mr-2"></Avatar>
                <span v-if="legalBasis.updated_by" qid="view-lb-modified-by">
                {{ legalBasis.updated_by.name }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="card">
      <div class="card-body">
        <div class="content-view-body">
          <br>
          <div class="mt-3">
            <p class="text-dark">
              {{ $t('maintenance.legal_basis.create.short_name_label') }}
            </p>
            <hr>
            <p qid="lb-short-name">{{ legalBasis.short_name }}</p>
            <br>
          </div>
          <div class="mt-3">
            <p class="text-dark">
              {{ $t('maintenance.legal_basis.create.description_label') }}
            </p>
            <hr>
            <p qid="lb-description" class="text-formatted">{{ legalBasis.description }}</p>
            <br>
          </div>
          <div class="mt-3">
            <p class="text-dark">
              {{ $t('maintenance.legal_basis.create.category_label') }}
            </p>
            <hr>
            <p qid="lb-category">{{ legalBasis.category }}</p>
            <br>
          </div>
        </div>
      </div>
    </div>


    <!-- Edit legal basis modal template -->
    <b-modal ref="edit-legal-basis" hide-footer>
      <div class="d-block text-center">
        <h4 qid="update-lb-title">
          {{ $t('maintenance.legal_basis.update.title') }}
        </h4>
      </div>
      <b-alert v-if="error.length"
               show
               variant="danger"
               qid="edit-lb-error"
      >
        {{ error }}
      </b-alert>
      <b-form @submit="editLBFormSubmit" qid="create-lb-form">
        <div class="form-container">
          <b-form-group>
            <label qid="edit-lb-name-label">
              {{ $t('maintenance.legal_basis.create.name_label') }}
            </label>
            <b-form-input
              qid="edit-lb-form-name"
              type="text"
              v-model="lbForm.name"
              :maxLength="255"
              required
              :placeholder="$t('maintenance.legal_basis.create.name_placeholder')"
            ></b-form-input>
          </b-form-group>
          <b-form-group>
            <label qid="edit-lb-short-name-label">
              {{ $t('maintenance.legal_basis.create.short_name_label') }}
            </label>
            <b-form-input
              qid="edit-lb-form-short-name"
              type="text"
              v-model="lbForm.short_name"
              required
              :placeholder="$t('maintenance.legal_basis.create.short_name_placeholder')"
            ></b-form-input>
          </b-form-group>
          <b-form-group>
            <label qid="edit-lb-description-label">
              {{ $t('maintenance.legal_basis.create.description_label') }}
            </label>
            <b-form-textarea
              qid="edit-lb-form-description"
              v-model="lbForm.description"
              rows="6"
              max-rows="6"
              required
              :placeholder="$t('maintenance.legal_basis.create.description_placeholder')"
            ></b-form-textarea>
          </b-form-group>
          <b-form-group :class="{'multiselect--show-input': categoryInput.length}">
            <label qid="edit-lb-category-label">
              {{ $t('maintenance.legal_basis.create.category_label') }}
            </label>
            <multiselect
                v-model="lbForm.category"
                :options="categoryItems"
                :placeholder="$t('maintenance.legal_basis.create.category_placeholder')"
                :preserveSearch="true"
                :select-label="$t('system.dropdown_select_label')"
                :selected-label="$t('system.dropdown_selected_label')"
                :deselect-label="$t('system.dropdown_deselect_label')"
                @search-change="handleCategoryInput"
                track-by="category"
                label="category"
                qid="edit-lb-form-category"
            >
              <template slot="noResult" slot-scope="props">
                {{ $t('maintenance.legal_basis.create.category_no_results') }}
              </template>
            </multiselect>
          </b-form-group>
        </div>
        <div class="text-right">
          <b-button class="mt-3"
                    variant="secondary"
                    @click="hideEditLBModal"
                    qid="edit-lb-cancel-button"
          >
            {{ $t('maintenance.legal_basis.create.cancel') }}
          </b-button>
          <b-button type="submit"
                    class="mt-3 ml-3"
                    variant="success"
                    :disabled="buttonDisabled"
                    qid="edit-lb-submit-button"
          >
            <span v-if="buttonDisabled" class="mr-1">
              <font-awesome-icon icon="spinner" size="sm" class="fa-spin"/>
            </span>
            {{ $t('maintenance.legal_basis.create.submit') }}
          </b-button>
        </div>
      </b-form>
    </b-modal>


    <!-- Delete legal basis confirm modal template -->
    <b-modal ref="delete-legal-basis" hide-footer>
      <div class="d-block text-center">
        <h4 qid="delete-lb-title">
          {{ $t('maintenance.legal_basis.delete.title') }}
        </h4>
      </div>
      <h5 class="text-center">
        {{ $t('maintenance.legal_basis.delete.description') }} {{ itemToDelete.name }}
      </h5>
      <h5 class="text-center">
        {{ $t('maintenance.legal_basis.delete.are_you_sure') }}
      </h5>
      <div class="text-center">
        <b-button class="mt-3"
                  variant="secondary"
                  @click="hideConfirmDeleteModal"
                  qid="delete-lb-cancel-button"
        >
          {{ $t('maintenance.legal_basis.delete.cancel') }}
        </b-button>
        <b-button @click="confirmDeleteLB(itemToDelete)"
                  class="mt-3 ml-3"
                  variant="success"
                  qid="delete-lb-submit-button"
                  v-if="isGranted([permissions.LEGAL_BASIS_DELETE, permissions.LEGAL_BASIS_DELETE_OWN], itemToDelete.created_by) && (!getClient.parent || (getClient.parent && itemToDelete.partition))"
        >
          {{ $t('maintenance.legal_basis.delete.submit') }}
        </b-button>
      </div>
    </b-modal>

  </div>
</template>

<script>
import {initialsAvatarUrl} from '@/util/userAvatar.js';
import piincHttp from '@/util/configureAxios.js'
import {config} from '@/config.js'
import Avatar from '@/components/Avatar.vue';
import GuideButton from '@/components/guide/GuideButton.vue';


export default {
  name: 'LegalBasisView',
  data: function() {
    return {
      breadcrumbItems: [
        {
          text: this.$t('dashboard.title'),
          to: '/'
        },
        {
          text: this.$t('maintenance.title'),
          to: '/maintenance'
        },
        {
          text: this.$t('maintenance.legal_basis.title'),
          to: '/maintenance/legal-basis'
        }
      ],
      legalBasis: {},
      showInput: false,
      showSingleLinkLabel: true,
      inputLink: '',
      itemToDelete: {},
      lbForm: {
        slug: '',
        name: '',
        short_name: '',
        description: '',
        category: [],
        client:'',
      },
      error: '',
      categoryItems: [],
      categoryInput: '',
      buttonDisabled: false,
      myGuides: [],
    }
  },
  computed: {
    rows() {
      // Use computed value for rows because of dynamic update
      return this.totalRows
    },
    getClient() {
      return this.$store.state.client
    }
  },
  watch: {
    // Watch when client changes in store and trigger api reload
    getClient: function (newValue, oldValue) {
      if (newValue && oldValue && newValue.slug !== oldValue.slug) {
        this.goToList()
      } else {
        this.loadContent();
        this.loadCategories();
      }
    }
  },
  components: {
    Avatar,
    GuideButton
  },
  methods: {
    goToList() {
      this.$router.push('/maintenance/legal-basis')
    },
    handleCategoryInput(input) {
      this.categoryInput = input;
    },
    getGuide(code) {
      let existingGuide = _.find(this.myGuides, function(object) {
        return object.scope == code && object.type == 'Maintenance' && object.enabled
      });
      return existingGuide
    },
    loadGuides(code) {
      let self = this;
      let language = localStorage.getItem('lang')? localStorage.getItem('lang') : 'en';

      piincHttp.get('guides', { params:
        {
          per_page: 1,
          language: 'en',
          type: 'Maintenance',
          scope: code,
          client: this.getClient.slug
        }
      }).then(function(response) {
        if (response.data.items.length) {
          self.myGuides.push(response.data.items[0])
        }
      }, function() {});
    },
    loadContent() {
      let self = this;
      piincHttp.get('articles/' + this.$route.params.slug, { params:
        {
          client: this.getClient.slug
        }
      }).then(function(response) {
        self.legalBasis = response.data;
        // Push data subject name in breadcrumb array
        self.breadcrumbItems.push({text: self.legalBasis.name, active: true});
      }, function() {});
    },
    getUserAvatar(user){
      if (user && user.avatar) {
        return user.avatar;
      } else {
        return initialsAvatarUrl(user);
      }
    },
    showConfirmDeleteModal(object) {
      this.itemToDelete = object
      this.$refs['delete-legal-basis'].show()
    },
    hideConfirmDeleteModal() {
      this.itemToDelete = {}
      this.$refs['delete-legal-basis'].hide()
    },
    confirmDeleteLB(object) {
      let self = this;
      piincHttp.delete('articles/' + object.slug).then(function() {
        self.hideConfirmDeleteModal();
        self.goToList();
        // Trigger toastr message after modal close
        self.$toastr('success', self.$t('maintenance.legal_basis.delete.success'))
      }, function() {});
    },
    showEditLBModal(object) {

      let self = this;

      this.lbForm.slug = object.slug;
      this.lbForm.name = object.name;
      this.lbForm.short_name = object.short_name;
      this.lbForm.description = object.description;

      // Preselect category on edit modal
      if (object.category) {
        this.lbForm.category = []
        _.find(this.categoryItems, function(item) {
          if (item.category == object.category) {
            self.lbForm.category = item;
          }
        });
      }

      this.$refs['edit-legal-basis'].show()
    },
    hideEditLBModal() {
      this.categoryInput = ''
      this.$refs['edit-legal-basis'].hide()
    },
    editLBFormSubmit(evt) {
      this.buttonDisabled = true;
      var moment = require('moment');
      // Keep the reference in a variable
      // to use it inside function called when promise returned
      let self = this;
      evt.preventDefault();
      this.lbForm.client = this.getClient.slug;

      if (this.categoryInput && this.categoryInput.length) {
        this.lbForm.category = this.categoryInput
      } else {
        if (this.lbForm.category && this.lbForm.category.category) {
          this.lbForm.category = this.lbForm.category.category
        } else {
          this.lbForm.category = '';
        }
      }

      piincHttp.put('articles/' + this.lbForm.slug, this.lbForm).then(function(response) {
        self.hideEditLBModal();
        self.buttonDisabled = false;
        // If slug is not changed, just reload content
        if (response.data.slug === self.legalBasis.slug) {
          // Remove last item in breadcrumbs, because it will be added again on load content
          self.breadcrumbItems.pop();
          self.loadContent();
        } else {
          // If slug changed, reload state with new param
          self.$router.push({ name: 'maintenance.legal_basis.view', params: { slug: response.data.slug }})
        }
        self.$toastr('success', self.$t('maintenance.legal_basis.update.success'))
        self.loadCategories();
      }, function(error) {
        if (error.status !== 200) {
          self.buttonDisabled = false;
          self.error = error.data.message;
          if (error.data.errors.category) {
            self.error += ' ' + error.data.errors.category[0]
          }
        }
      })
    },
    loadCategories() {
      let self = this;
      piincHttp.get('/articles/categories', { params:
        {
          client: this.getClient.slug
        }
      }).then(function(response) {
        self.categoryItems = response.data
        if (self.categoryItems && !self.categoryItems.length) {
          self.categoryItems.push({category: self.$t('system.no_results'), $isDisabled: true})
        }
      }, function() {});
    }
  },
  created () {
    if (this.getClient && this.getClient.slug) {
      this.loadContent();
      this.loadCategories();
    }
    this.loadGuides('legal_basis');
  }
}
</script>
